import {createApp} from "vue";
import {createI18n} from "vue-i18n";
import App from "./App.vue";

// markdown renderer
import markdown from "@/plugin/markdown";
// i18n
import messages from "./plugin/i18n/locales";
// icons
import icons from "./plugin/icons/index";
// router
import router from "@/plugin/router";
// store
import store from "@/plugin/store";
// echarts
import echarts from "@/plugin/echarts";
// axios
import axios from "axios";
import VueAxios from "vue-axios";
// idp
import idp from "@/plugin/idp";
// app version
import version from "@/plugin/version";
// css
import "./sass/styles.scss";
// vuetify
import {VDateInput} from "vuetify/labs/VDateInput";
import {VStepperVertical, VStepperVerticalItem} from "vuetify/labs/VStepperVertical";

import {aliases, mdi} from "vuetify/iconsets/mdi";
import {createVuetify} from "vuetify";
import {it} from "vuetify/locale";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_API;
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest = error.config;
        if (error.response != null && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            let failedPath = window.location.pathname + window.location.search;

            return idp
                .getAuthenticator(null, store.getRefreshToken())
                .then(() => axios(originalRequest))
                .catch(() => {
                    store.clear();
                    window.location.href = idp.getLoginURL(failedPath);
                });
        }
        return Promise.reject(error);
    }
);

const i18n = createI18n({
    legacy: false, // Enable Composition API integration
    locale: store.getLocale() != null ? store.getLocale() : "it", // Set the default locale
    messages, // Pass your translation object
    datetimeFormats: {
        it: {
            short: {year: "numeric", month: "numeric", day: "numeric"},
            long: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: false
            },
            year: {year: "numeric"},
            month: {year: "numeric", month: "numeric"},
            hour: {hour: "numeric", minute: "numeric", hour12: false}
        },
        en: {
            short: {year: "numeric", month: "numeric", day: "numeric"},
            long: {year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", hour12: true},
            hour: {hour: "numeric", minute: "numeric", hour12: true}
        }
    }
});
const vuetify = createVuetify({
    components: {
        VDateInput,
        VStepperVertical,
        VStepperVerticalItem
    },
    locale: {
        locale: "it",
        messages: {it}
    },
    defaults: {
        VCheckbox: {
            color: "primary"
        },
        VContainer: {
            style: "max-width: calc(95%)"
        },
        VCheckboxBtn: {
            color: "primary"
        },
        VChip: {
            color: "primary"
        },
        VWindow: {
            style: "text-transform: none"
        },
        VStepperVertical: {
            bgColor: "background"
        }
    },
    icons: {
        defaultSet: "mdi",
        aliases,
        sets: {
            mdi
        }
    },
    theme: {
        defaultTheme: "light",
        options: {
            customProperties: true
        },
        themes: {
            light: {
                dark: false,
                colors: {
                    background: "#E8E8E4",
                    surface: "#FFFFFF",
                    primary: "#003247",
                    "primary-darken-1": "#003247",
                    secondary: "#558a9f",
                    "secondary-darken-1": "#335E6F",
                    error: "#B00020",
                    info: "#2196F3",
                    success: "#4CAF50",
                    warning: "#FB8C00",
                    ai: "#744ECCE6"
                }
            }
        }
    }
});

// create app
const app = createApp(App);
// constants
// ENUM
app.provide("reportVersions", {
    V1: "V1",
    V2: "V2"
});
app.provide("reportStatuses", {
    CREATED: "CREATED",
    IN_PROGRESS: "IN_PROGRESS",
    FAILED: "FAILED"
});
app.provide("chatStatuses", {
    IN_PROGRESS: "IN_PROGRESS",
    READY: "READY"
});

// ICONS
app.provide("icons", icons);
//  FILE STATUS
const fileStatusReady = {value: "READY", icon: icons.vaults.fileStatuses.READY, color: "primary"};
const fileStatusTagging = {value: "TAGGING", icon: icons.vaults.fileStatuses.TAGGING, color: "primary"};
const fileStatusArchived = {value: "ARCHIVED", icon: icons.vaults.fileStatuses.ARCHIVED, color: "primary"};
const fileStatusIndexing = {value: "INDEXING", icon: icons.vaults.fileStatuses.INDEXING, color: "primary"};
const fileStatusIndexed = {value: "INDEXED", icon: icons.vaults.fileStatuses.INDEXED, color: "success"};
const fileStatusError = {value: "ERROR", icon: icons.vaults.fileStatuses.ERROR, color: "warning"};
app.provide("fileStatusReady", fileStatusReady);
app.provide("fileStatusTagging", fileStatusTagging);
app.provide("fileStatusArchived", fileStatusArchived);
app.provide("fileStatusIndexing", fileStatusIndexing);
app.provide("fileStatusIndexed", fileStatusIndexed);
app.provide("fileStatusError", fileStatusError);
app.provide("fileStatuses", [
    fileStatusReady,
    fileStatusTagging,
    fileStatusArchived,
    fileStatusIndexing,
    fileStatusIndexed,
    fileStatusError
]);
app.provide("formatFileSize", (bytes) => {
    if (bytes == null) {
        return bytes;
    }

    let units = ["bytes", "KB", "MB", "GB", "TB"];
    let factor = 1000;
    let currentValue = bytes;

    let unitFound = currentValue < factor;
    let index = 0;
    while (!unitFound) {
        currentValue /= factor;
        index++;
        unitFound = currentValue < factor;
    }

    return `${parseFloat(currentValue).toFixed(2)} ${units[index]}`;
});
app.provide("isDev", () => process.env.NODE_ENV === "development");

app.provide("refreshTime", 10000);
app.provide("fastRefreshTime", 5000);
// markdown renderer
app.use(markdown);
// i18n
app.use(i18n);
app.provide("i18n");
// echarts
app.component("v-chart", echarts);
// vuetify
app.use(vuetify);
// vue router
app.use(router);
// app version (needs router)
app.use(version, {router});
// axios
app.use(VueAxios, axios);
app.provide("axios", axios);
// idp
app.use(idp);
// store
app.use(store);
// disable warning for tip-tap
// https://github.com/ueberdosis/tiptap/issues/1719
app.config.unwrapInjectedRef = true;
// mount app
app.mount("#app");
